import { ApiError } from "@groundspark/browser-auth";
import { CanvassingAccess } from "./access";
import {
  type House,
  type Position,
  type Project,
  type Road,
  type Selection,
  type StreetRecord,
  type Voter,
  dateLabel,
} from "./domain";
import { dirty } from "./drafts";
import { answerBadges, ElectorEditor } from "./elector-editor";
import { SessionLocation } from "./location";
import type { CanvassMap } from "./map";
import { StreetState } from "./street-state";
import {
  actions,
  button,
  element,
  errorMessage,
  heading,
  Lifetime,
  message,
  notice,
  panel,
  runButton,
  selectField,
  type View,
} from "./ui";

function selectionValue(value: Selection): string {
  return `${value.kind}:${value.id}`;
}
function sessionLabel(access: CanvassingAccess, id: string): string {
  const session = access.sessions.find((session) => session.id === id)!;
  return `Session ${session.project.name} · ${new Date(session.started_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

export class CanvassView implements View {
  public readonly element = element("div", "canvassing-view");
  private readonly lifetime = new Lifetime();
  private readonly selector = element("div", "workspace-selector");
  private readonly pending = element("div", "pending-drafts");
  private readonly content = element("div", "workspace-content");
  private workspace: Workspace | null = null;
  private key: string | null = null;
  private selectorKey = "";
  private cleanup: Promise<void> = Promise.resolve();
  public constructor(private readonly access: CanvassingAccess) {
    this.element.append(this.selector, this.pending, this.content);
    this.lifetime.add(access.subscribe(() => this.render()));
    this.render();
  }
  private render(): void {
    if (!this.lifetime.active) return;
    const selection = this.access.selection;
    const project = this.access.currentProject;
    const options: [string, string][] = [
      ["", "Choose a session or project"],
      ...this.access.sessions.map((session): [string, string] => [
        `session:${session.id}`,
        sessionLabel(this.access, session.id),
      ]),
      ...this.access.projects.map((project): [string, string] => [
        `project:${project.id}`,
        `Project ${project.name} (READ ONLY)`,
      ]),
    ];
    const selectorKey = JSON.stringify([options, selection]);
    if (selectorKey !== this.selectorKey) {
      this.selectorKey = selectorKey;
      const choice = selectField(
        "Canvassing workspace",
        options,
        selection ? selectionValue(selection) : "",
      );
      choice.select.addEventListener("change", () => {
        const [kind, id] = choice.select.value.split(":");
        this.access.select(
          (kind === "session" || kind === "project") && id ? { kind, id } : null,
        );
      });
      this.selector.replaceChildren(choice.label);
      if (selection?.kind === "project")
        this.selector.append(element("strong", "readonly-label", "READ ONLY"));
    }
    const key = project && selection ? `${selectionValue(selection)}:${project.id}` : null;
    if (key !== this.key) {
      const old = this.workspace;
      this.workspace = null;
      if (old) {
        const cleanup = old.dispose();
        this.cleanup = Promise.all([this.cleanup, cleanup]).then(() => {});
      }
      this.content.replaceChildren();
      this.key = key;
      if (key && project && selection) {
        this.workspace = new Workspace(this.access, project, selection, () =>
          this.renderPending(),
        );
        this.content.append(this.workspace.element);
        this.workspace.mount();
      }
    }
    if (key === null) {
      const choices = panel(
        heading(1, "Go canvassing"),
        notice(
          "Select an active session. Organisers can also review a project without editing.",
        ),
      );
      for (const session of this.access.sessions)
        choices.append(
          button(sessionLabel(this.access, session.id), "choice-row", () =>
            this.access.select({ kind: "session", id: session.id }),
          ),
        );
      for (const project of this.access.projects)
        choices.append(
          button(`Project ${project.name} (READ ONLY)`, "choice-row", () =>
            this.access.select({ kind: "project", id: project.id }),
          ),
        );
      if (!this.access.sessions.length && !this.access.projects.length)
        choices.append(
          notice(
            this.access.initialized
              ? "No accessible sessions or projects. Join or start a session to begin."
              : "Loading access…",
          ),
        );
      this.content.replaceChildren(choices);
    }
    this.renderPending();
  }
  private renderPending(): void {
    this.pending.replaceChildren();
    for (const { project, drafts } of this.access.pendingProjects) {
      const count = drafts.pending.length;
      const error = errorMessage();
      this.pending.append(
        element(
          "div",
          "pending-drafts__project",
          element(
            "span",
            null,
            `${project.name}: ${count} voter ${count === 1 ? "edit" : "edits"} pending in memory.`,
          ),
          runButton(
            "Retry saves",
            async () => {
              await drafts.flush();
              this.renderPending();
            },
            error,
          ),
          error,
        ),
      );
    }
    this.pending.hidden = this.pending.children.length === 0;
  }
  public dispose(): Promise<void> {
    this.lifetime.dispose();
    const cleanup = this.workspace?.dispose() ?? Promise.resolve();
    this.workspace = null;
    this.content.replaceChildren();
    return Promise.all([this.cleanup, cleanup]).then(() => {});
  }
}

class Workspace {
  public readonly element = element("div", "workspace");
  private readonly lifetime = new Lifetime();
  private readonly mapContainer = element("div", "canvass-map");
  private readonly mapError = errorMessage();
  private readonly locationNotice = notice("Finding your location…");
  private readonly body = element("div", "canvass-list");
  private readonly error = errorMessage();
  private readonly loading = notice();
  private map: CanvassMap | null = null;
  private location: SessionLocation | null = null;
  private position: Position | null = null;
  private searchPosition: Position | null = null;
  private roads: readonly Road[] = [];
  private selectedRoad: Road | null = null;
  private houses: readonly House[] = [];
  private selectedVoter: Voter | null = null;
  private editor: ElectorEditor | null = null;
  private street: StreetState | null = null;
  private roadsGeneration = 0;
  private detailGeneration = 0;
  private following = true;
  private readonly readonly: boolean;
  private readonly drafts;

  public constructor(
    private readonly access: CanvassingAccess,
    private readonly project: Project,
    private readonly selection: Selection,
    onDrafts: () => void,
  ) {
    this.readonly = selection.kind === "project";
    this.drafts = access.projectDrafts(project.id);
    this.lifetime.add(
      this.drafts.subscribe(() => {
        onDrafts();
        this.updateVoterBadges();
      }),
    );
    const locate = button("My location", "button button--secondary", () => {
      this.following = true;
      if (this.position) {
        this.map?.setPosition(this.position, true);
        void this.findRoads(this.position);
      } else
        this.locationNotice.textContent =
          "Enable location access in your browser settings. You can also pan the map and search an area.";
    });
    const search = runButton(
      "Search this area",
      async () => {
        if (!this.map)
          throw new Error(
            "The map is unavailable. Enable location access to find nearby streets.",
          );
        this.following = false;
        await this.findRoads(this.map.center);
      },
      this.error,
      "button button--secondary",
    );
    const controls = actions(
      locate,
      search,
      runButton("Refresh", () => this.refresh(true), this.error, "button button--secondary"),
    );
    if (!this.readonly) {
      const share = element("input");
      share.type = "checkbox";
      share.checked = true;
      share.addEventListener("change", () => {
        this.location?.setSharing(share.checked);
        this.locationNotice.textContent = share.checked
          ? "Your location is shared with this session while this view is open."
          : "Location sharing paused.";
      });
      controls.append(element("label", "checkbox-field", share, "Share location with session"));
    }
    this.element.append(
      element("div", "map-area", this.mapContainer, this.mapError),
      controls,
      this.locationNotice,
      this.error,
      this.loading,
      this.body,
    );
    const timer = setInterval(() => void this.refresh(false), 15_000);
    this.lifetime.add(() => clearInterval(timer));
  }
  public mount(): void {
    void this.mountMap();
    this.startLocation();
    this.renderRoads();
  }
  private async mountMap(): Promise<void> {
    try {
      const { CanvassMap } = await import("./map");
      if (!this.lifetime.active) return;
      this.map = new CanvassMap(
        this.mapContainer,
        this.access.api.transport,
        (failure) => {
          if (this.lifetime.active) this.mapError.textContent = `Map: ${message(failure)}`;
        },
        (usrn) => {
          const road = this.roads.find((road) => road.usrn === usrn);
          if (road) void this.selectRoad(road);
        },
      );
      if (this.position) this.map.setPosition(this.position, true);
      this.updateMap();
    } catch (failure) {
      if (this.lifetime.active)
        this.mapError.textContent = `Map could not start: ${message(failure)}`;
    }
  }
  private startLocation(): void {
    this.location = new SessionLocation(
      this.access.api,
      this.selection.kind === "session" ? this.selection.id : null,
      (position) => {
        if (!this.lifetime.active) return;
        const first = this.position === null;
        this.position = position;
        this.map?.setPosition(position, first);
        this.locationNotice.textContent = this.readonly
          ? "Location stays on this device in a read-only project."
          : "Your location is shared with this session while this view is open.";
        if (this.following && (!this.searchPosition || moved(position, this.searchPosition)))
          void this.findRoads(position);
      },
      (peers) => {
        if (this.lifetime.active)
          this.map?.setPeers(
            peers.filter(({ user }) => user.id !== this.access.account.user.id),
          );
      },
      (failure) => {
        if (!this.lifetime.active) return;
        this.locationNotice.textContent = message(failure);
        if (failure instanceof ApiError && (failure.status === 403 || failure.status === 404))
          void this.access.refreshSessions(true);
      },
    );
    this.location.start();
  }
  private async findRoads(position: Position): Promise<void> {
    const generation = ++this.roadsGeneration;
    this.loading.textContent = "Loading nearby streets…";
    this.error.textContent = "";
    try {
      const roads = await this.access.api.nearby(
        this.project.id,
        position,
        this.project.electoral_register_id === null,
        this.lifetime.abort.signal,
      );
      if (!this.lifetime.active || generation !== this.roadsGeneration) return;
      this.searchPosition = position;
      this.roads = roads;
      if (this.selectedRoad)
        this.selectedRoad =
          roads.find(({ usrn }) => usrn === this.selectedRoad!.usrn) ?? this.selectedRoad;
      this.updateMap();
      if (!this.selectedRoad) this.renderRoads();
    } catch (failure) {
      this.fail(failure, generation === this.roadsGeneration);
    } finally {
      if (this.lifetime.active && generation === this.roadsGeneration)
        this.loading.textContent = "";
    }
  }
  private renderRoads(): void {
    this.body.replaceChildren(heading(2, "Nearby streets"));
    if (!this.searchPosition)
      this.body.append(
        notice("Allow location access or pan the map and choose Search this area."),
      );
    else if (!this.roads.length)
      this.body.append(notice("No streets found nearby. Try another area."));
    for (const road of this.roads) {
      const item = button(
        "",
        `road-row${road.canvass?.completed ? " road-row--completed" : ""}`,
        () => this.selectRoad(road),
      );
      item.append(
        element(
          "span",
          null,
          element("strong", null, road.name),
          element("small", null, `USRN ${road.usrn} · ${Math.round(road.distance_meters)} m`),
        ),
        element("span", "road-status", road.canvass?.completed ? "✓ Completed" : "→"),
      );
      this.body.append(item);
    }
  }
  private async selectRoad(road: Road): Promise<void> {
    const generation = ++this.detailGeneration;
    this.editor?.dispose();
    this.editor = null;
    this.street?.dispose();
    this.street = null;
    this.selectedVoter = null;
    this.houses = [];
    this.selectedRoad = road;
    this.map?.select(road);
    if (this.project.electoral_register_id === null) {
      const street = new StreetState(
        road.canvass,
        () =>
          this.access.api
            .street(this.project.id, road.usrn, this.lifetime.abort.signal)
            .catch((failure: unknown) => {
              this.fail(failure);
              throw failure;
            }),
        (completed, revision) =>
          this.access.api
            .saveStreet(
              this.project.id,
              road.usrn,
              completed,
              revision,
              this.lifetime.abort.signal,
            )
            .catch((failure: unknown) => {
              this.fail(failure);
              throw failure;
            }),
        () => {
          if (!this.lifetime.active || generation !== this.detailGeneration) return;
          if (street.record) this.replaceStreet(street.record);
          this.renderStreet();
        },
      );
      this.street = street;
      this.renderStreet();
      await street.refresh();
      return;
    }
    this.body.replaceChildren(
      this.backToRoads(),
      heading(2, road.name),
      notice("Loading households…"),
    );
    try {
      const position = this.searchPosition ?? this.position;
      if (!position) throw new Error("Choose a map area or allow location access first.");
      const houses = await this.access.api.houses(
        this.project.id,
        position,
        road.usrn,
        this.lifetime.abort.signal,
      );
      if (!this.lifetime.active || generation !== this.detailGeneration) return;
      this.houses = houses;
      this.renderHouses();
    } catch (failure) {
      this.fail(failure, generation === this.detailGeneration);
    }
  }
  private backToRoads(): HTMLButtonElement {
    return button("← Nearby streets", "button button--text", () => {
      this.detailGeneration += 1;
      this.editor?.dispose();
      this.editor = null;
      this.street?.dispose();
      this.street = null;
      this.selectedVoter = null;
      this.selectedRoad = null;
      this.houses = [];
      this.map?.select(null);
      this.renderRoads();
    });
  }
  private renderHouses(): void {
    if (!this.selectedRoad) return;
    this.body.replaceChildren(
      this.backToRoads(),
      heading(2, this.selectedRoad.name),
      element("p", "muted", `USRN ${this.selectedRoad.usrn}`),
    );
    if (!this.houses.length)
      this.body.append(
        notice(
          "No resolved electoral-register addresses for this street. Unmatched addresses are never guessed from a street name.",
        ),
      );
    for (const house of this.houses) {
      const card = element(
        "section",
        "house",
        heading(3, house.address_lines.join(", ")),
        element("p", "muted", house.postcode),
      );
      for (const voter of house.registered_voters) {
        const row = button("", "voter-row", () => this.openVoter(voter));
        row.dataset.elector = voter.elector_id;
        row.append(
          element("strong", null, voter.name),
          answerBadges(this.drafts.get(voter.elector_id) ?? voter.canvass),
        );
        card.append(row);
      }
      this.body.append(card);
    }
  }
  private updateVoterBadges(): void {
    if (!this.lifetime.active) return;
    for (const row of this.body.querySelectorAll<HTMLElement>("[data-elector]")) {
      const draft = this.drafts.get(row.dataset.elector!);
      if (!draft) continue;
      row.querySelector(".answer-badges")?.replaceWith(answerBadges(draft));
      row.classList.toggle("voter-row--pending", dirty(draft));
    }
  }
  private async openVoter(voter: Voter): Promise<void> {
    const generation = ++this.detailGeneration;
    this.editor?.dispose();
    this.editor = null;
    this.selectedVoter = voter;
    this.body.replaceChildren(
      button("← Households", "button button--text", () => {
        this.detailGeneration += 1;
        this.selectedVoter = null;
        this.editor?.dispose();
        this.editor = null;
        this.renderHouses();
      }),
      heading(2, voter.name),
      notice("Loading result…"),
    );
    try {
      const existing = this.drafts.get(voter.elector_id);
      if (!existing || !dirty(existing)) {
        const record = await this.access.api.elector(
          this.project.id,
          voter.elector_id,
          this.lifetime.abort.signal,
        );
        if (!this.lifetime.active || generation !== this.detailGeneration) return;
        this.drafts.load(record);
      }
      if (!this.lifetime.active || generation !== this.detailGeneration) return;
      this.editor = new ElectorEditor(this.drafts, voter, this.readonly);
      this.body.replaceChildren(
        button("← Households", "button button--text", () => {
          this.detailGeneration += 1;
          this.selectedVoter = null;
          this.editor?.dispose();
          this.editor = null;
          this.renderHouses();
        }),
        this.editor.element,
      );
    } catch (failure) {
      this.fail(failure, generation === this.detailGeneration);
    }
  }
  private renderStreet(): void {
    const street = this.street;
    const road = this.selectedRoad;
    if (!street || !road) return;
    const record = street.record;
    const status = record?.completed ? "Completed" : "Not completed";
    const content = panel(
      heading(2, road.name),
      element("p", "muted", `USRN ${road.usrn}`),
      heading(3, status),
      notice("Completion covers the entire street, including any part beyond the visible map."),
    );
    if (record?.changed_at)
      content.append(
        element(
          "p",
          "muted",
          `${record.completed ? "Completed" : "Undone"} by ${record.user_id} · ${dateLabel(record.changed_at)}`,
        ),
      );
    if (street.error) content.append(element("p", "error", message(street.error)));
    if (street.conflict)
      content.append(
        element(
          "p",
          "error",
          "This street changed elsewhere. Refresh its status before submitting again.",
        ),
      );
    const refresh = button(
      street.busy ? "Refreshing…" : "Refresh status",
      "button button--secondary",
      () => street.refresh(true),
      street.busy,
    );
    const controls = actions(refresh);
    if (!this.readonly)
      controls.prepend(
        button(
          street.busy ? "Saving…" : record?.completed ? "Undo" : "Mark as completed",
          "button",
          () => street.toggle(),
          street.busy || street.conflict || record === null,
        ),
      );
    content.append(controls);
    this.body.replaceChildren(this.backToRoads(), content);
  }
  private replaceStreet(record: StreetRecord): void {
    this.roads = this.roads.map((road) =>
      road.usrn === record.usrn ? { ...road, canvass: record } : road,
    );
    if (this.selectedRoad?.usrn === record.usrn)
      this.selectedRoad = { ...this.selectedRoad, canvass: record };
    this.updateMap();
  }
  private updateMap(): void {
    const selected = this.selectedRoad;
    const roads =
      selected && !this.roads.some(({ usrn }) => usrn === selected.usrn)
        ? [...this.roads, selected]
        : this.roads;
    this.map?.setRoads(roads, selected?.usrn ?? null);
  }
  private async refresh(manual: boolean): Promise<void> {
    if (!this.lifetime.active) return;
    if (this.street) await this.street.refresh(manual);
    if (this.searchPosition) await this.findRoads(this.searchPosition);
    if (
      manual &&
      this.selectedRoad &&
      this.project.electoral_register_id !== null &&
      !this.selectedVoter
    )
      await this.selectRoad(this.selectedRoad);
  }
  private fail(failure: unknown, current = true): void {
    if (!this.lifetime.active || !current) return;
    this.access.handleProjectError(this.project.id, failure);
    if (this.lifetime.active) this.error.textContent = message(failure);
  }
  public dispose(): Promise<void> {
    this.lifetime.dispose();
    this.editor?.dispose();
    this.editor = null;
    this.street?.dispose();
    this.street = null;
    this.roads = [];
    this.houses = [];
    this.selectedRoad = null;
    this.selectedVoter = null;
    this.position = this.searchPosition = null;
    this.map?.dispose();
    this.map = null;
    this.body.replaceChildren();
    void this.drafts.flush();
    return this.location?.dispose() ?? Promise.resolve();
  }
}

function moved(a: Position, b: Position): boolean {
  const north = (a.latitude - b.latitude) * 111_000;
  const east = (a.longitude - b.longitude) * 111_000 * Math.cos((a.latitude * Math.PI) / 180);
  return north * north + east * east > 35 * 35;
}
